// In-app intake entry point (docs/app.md → Upload). Drag-and-drop a PDF to
// start a new invoice. Send is one call per file: `uploadDocument` asks the API
// where to put the file, PUTs the bytes straight to storage, then registers the
// upload under an idempotency key so a retry cannot create a second row or a
// second extraction charge. The extraction pipeline takes over from there.
//
// The old flow took the signed-in user's id, email and name as arguments and
// reported success through a `flow_state` field; both are gone — the API knows
// who is calling, and a failure throws.
//
// Open to any signed-in user: every uploaded document goes through the whole
// pipeline, so restricting who may start one buys nothing.
import { useRef, useState } from 'react'
import {
  Button,
  Body1,
  Caption1,
  MessageBar,
  MessageBarBody,
  Spinner,
  Toast,
  ToastBody,
  ToastTitle,
  makeStyles,
  mergeClasses,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import {
  ArrowUploadRegular,
  CheckmarkCircleFilled,
  DocumentRegular,
  DeleteRegular,
  ErrorCircleFilled,
} from '@fluentui/react-icons'
import { errorMessage } from '../api/errors'
import { TOASTER_ID } from '../lib/constants'
import { uploadDocument } from '../lib/data'

const useStyles = makeStyles({
  // Body1 renders a span; block display so the bottom margin actually spaces.
  sub: { display: 'block', color: tokens.colorNeutralForeground3, marginBottom: '16px' },
  dropzone: {
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '48px 24px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  dropzoneActive: {
    border: `2px dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  list: { display: 'flex', flexDirection: 'column', gap: '8px', margin: '16px 0' },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  itemMain: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' },
  itemError: { color: tokens.colorPaletteRedForeground1 },
  // Breathing room below the drop zone (or staged-file list) before the actions.
  bar: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' },
  ok: { color: tokens.colorPaletteGreenForeground1 },
  fail: { color: tokens.colorPaletteRedForeground1 },
})

// PDF only: the API refuses anything else with `unsupported_media_type`, so the
// picker should not offer a file that will be rejected.
const ACCEPT = '.pdf'
const MAX_BYTES = 32 * 1024 * 1024

type ItemStatus = 'pending' | 'sending' | 'sent' | 'failed'

interface Item {
  file: File
  status: ItemStatus
  /** The API's own wording for why this file failed — shown on its own row. */
  error?: string
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function UploadScreen() {
  const s = useStyles()
  const [items, setItems] = useState<Item[]>([])
  const [dragging, setDragging] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  function addFiles(list: FileList | null) {
    if (!list) return
    const incoming = Array.from(list)
    const tooBig = incoming.filter((f) => f.size > MAX_BYTES)
    const ok = incoming.filter((f) => f.size <= MAX_BYTES)
    if (tooBig.length) {
      setError(
        `${tooBig.length === 1 ? `“${tooBig[0].name}” is` : `${tooBig.length} files are`} over the 32 MB limit and can’t be uploaded here. Email larger documents to the intake inbox instead.`,
      )
    } else if (ok.length) {
      setError(null)
    }
    if (ok.length) setItems((prev) => [...prev, ...ok.map((file): Item => ({ file, status: 'pending' }))])
  }

  function setStatus(index: number, status: ItemStatus, message?: string) {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, status, error: message } : it)))
  }

  // One upload per file, sequentially. Sent files leave the list; a failure stays
  // listed with its reason so it can be retried.
  async function handleSend() {
    setSending(true)
    setError(null)
    let sent = 0
    let failed = 0
    for (let i = 0; i < items.length; i++) {
      if (items[i].status === 'sent') continue
      setStatus(i, 'sending')
      try {
        await uploadDocument(items[i].file)
        setStatus(i, 'sent')
        sent++
      } catch (e) {
        setStatus(i, 'failed', errorMessage(e))
        failed++
      }
    }
    setSending(false)
    if (sent > 0) {
      dispatchToast(
        <Toast>
          <ToastTitle>
            {sent === 1 ? 'Your document has been received.' : 'Your documents have been received.'}
          </ToastTitle>
          <ToastBody>
            {sent === 1
              ? 'Please wait for just a minute or so while it is processed.'
              : 'Please wait for just a minute or so while they are processed.'}
          </ToastBody>
        </Toast>,
        { intent: 'success' },
      )
      // Drop the sent files; anything failed stays listed for retry.
      setItems((prev) => prev.filter((it) => it.status !== 'sent'))
    }
    if (failed > 0) {
      setError(
        `${failed === 1 ? '1 file' : `${failed} files`} could not be sent. Retry, or email the invoice to the intake inbox instead.`,
      )
    }
  }

  const pendingCount = items.filter((it) => it.status !== 'sent').length

  return (
    <div>
      <Body1 className={s.sub}>
        Drop invoice documents here to start the intake process. Whenever possible, please attach one invoice per document.
      </Body1>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <div
        className={mergeClasses(s.dropzone, dragging && s.dropzoneActive)}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          addFiles(e.dataTransfer.files)
        }}
      >
        <ArrowUploadRegular fontSize={40} />
        <Body1>Drag files here, or click to browse</Body1>
        <Caption1>PDF, up to 32 MB each</Caption1>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {items.length > 0 && (
        <div className={s.list}>
          {items.map((it, i) => (
            <div key={`${it.file.name}-${i}`} className={s.item}>
              {it.status === 'sending' ? (
                <Spinner size="tiny" />
              ) : it.status === 'sent' ? (
                <CheckmarkCircleFilled className={s.ok} />
              ) : it.status === 'failed' ? (
                <ErrorCircleFilled className={s.fail} />
              ) : (
                <DocumentRegular />
              )}
              <span className={s.itemMain}>
                <span>{it.file.name}</span>
                {it.error && <Caption1 className={s.itemError}>{it.error}</Caption1>}
              </span>
              <Caption1>{formatSize(it.file.size)}</Caption1>
              <Button
                appearance="subtle"
                size="small"
                icon={<DeleteRegular />}
                aria-label="Remove"
                disabled={sending}
                onClick={() => setItems((prev) => prev.filter((_, j) => j !== i))}
              />
            </div>
          ))}
        </div>
      )}

      <div className={s.bar}>
        <Button
          appearance="primary"
          icon={sending ? undefined : <ArrowUploadRegular />}
          disabled={pendingCount === 0 || sending}
          onClick={handleSend}
        >
          {sending ? 'Sending…' : `Send to intake${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
        </Button>
        {items.length > 0 && !sending && (
          <Button appearance="subtle" onClick={() => setItems([])}>Clear</Button>
        )}
      </div>
    </div>
  )
}
